import { FieldSchema, FieldValueObject, isEmpty, EMPTY_VALUE } from "../../../Schema";
import { GridFilterTypes } from "../../../grid/AgGridManager";
import { LinkedInstance } from "../../common/external-sources/LinkedInstances";
import { User } from "../../common/external-sources/Users";
import { getManufacturers } from "../../common/external-sources/CategoryMappings";
import { writeStoredText } from "../../common/fields/stored";
import { EntryTypes } from "../../common/fields";
import { Modes } from "../../common/fields/mode";
import { EquipmentContext } from "..";
import { validCategoryTuple } from "./categoryTuple";

// EquipmentContext, not SchemaContext: only an equipment has a manufacturer — a cluster
// records the placeholder — so this field is never built for the cluster schema, and the
// category mappings it validates against are captured on the equipment context alone.
export const createManufacturer = (context: EquipmentContext): FieldSchema => ({
  grid: {
    displayed: true,
    order: 5,
    defaultVisible: true,
    filter: GridFilterTypes.SET,
    // Set filter without a `data` enum: the form drives this through the MasterDataPicker, so
    // declaring options on `data` would change coercion and rendering for a filter's sake.
    filterValues: { getter: () => getManufacturers(context.client) },
  },
  name: "manufacturer",
  dataTransform: {
    // A cluster has no manufacturer of its own, and the cluster resolvers record that as the
    // placeholder rather than as nothing —
    // `Mutation.updateClusterDigitalLabInstrumentRepositoryEntry.req.vtl` does it alongside
    // `serialNumber` and `materialNumber`. It matters because the column is part of
    // `manufacturer#equipmentModel#serialNumber`, the sort key of
    // `IREntryByManufacturerModelAndSNo`: with nothing there the composite is never built and
    // the record is absent from the index. That is the state 662 dev and 354 pred records are
    // already in, every one of them a cluster.
    //
    // Only for a cluster. An equipment with no manufacturer has a required field left blank,
    // which validation should say so about rather than the write papering over — and the data
    // agrees: all 660 dev clusters hold the placeholder, and 1246 of 1246 subequipments and
    // 7602 of 7604 standalone equipments hold a real name.
    toDb: {
      getter: ({ entryType, manufacturer }) =>
        entryType === EntryTypes.CLUSTER ? writeStoredText(manufacturer) : manufacturer,
      fields: ["entryType", "manufacturer"],
    },
  },
  data: {
    assignedValue: {
      getter: ({ manufacturer, masterDataPicker, mode }) => {
        // See `equipmentCategory`: in a CSV the column wins over the picker rebuilt from
        // the stored record.
        if (masterDataPicker && mode !== Modes.CSV) {
          return (masterDataPicker as FieldValueObject)?.manufacturer ?? EMPTY_VALUE;
        }
        return isEmpty(manufacturer) ? EMPTY_VALUE : manufacturer;
      },
      fields: ["masterDataPicker", "mode"],
    },
  },
  display: {
    displayed: true,
  },
  validation: {
    required: true,
    custom: validCategoryTuple(context.categoryMappings),
  },
  form: {
    label: "Manufacturer",
    disabled: {
      getter: ({ entryType, linkedInstance, mode, currentUser }) => {
        return (
          [EntryTypes.STANDALONE_EQUIPMENT, EntryTypes.CLUSTER_SUBEQUIPMENT].includes(entryType as EntryTypes) &&          
          [Modes.EDIT, Modes.BULK_EDIT].includes(mode as Modes) &&
          ["P1S Uni-Directional"].includes((linkedInstance as LinkedInstance)?.linkedInstance)
        ) && !(currentUser as User)?.isAdmin;
      },
      fields: ["entryType", "linkedInstance", "mode", "currentUser"],
    },
  },
});
